import type { VeoClient } from '../../types';
import {
  DEFAULT_TRACKER_BATCH_SIZE,
  DEFAULT_TRACKER_FLUSH_INTERVAL_MS,
  DEFAULT_TRACKER_MAX_RETRIES,
} from './constants';
import { GuideFrequencyCache, namespaceFromApiKey } from './guide-frequency-cache';
import { GuideResolverClient } from './guide-resolver-client';
import { GuideTrackerClient } from './guide-tracker-client';
import { GuideTrackerQueue } from './guide-tracker-queue';
import { BadgeRenderer } from './renderers/badge-renderer';
import { BannerRenderer } from './renderers/banner-renderer';
import type { BaseRenderer } from './renderers/base-renderer';
import { CompositeRenderer } from './renderers/composite-renderer';
import { CustomRenderer } from './renderers/custom-renderer';
import { FormRenderer } from './renderers/form-renderer';
import { InlineCustomRenderer } from './renderers/inline-custom-renderer';
import { InlineFormRenderer } from './renderers/inline-form-renderer';
import { InlineRenderer } from './renderers/inline-renderer';
import { ModalRenderer } from './renderers/modal-renderer';
import { TooltipRenderer } from './renderers/tooltip-renderer';
import { WalkthroughRenderer } from './renderers/walkthrough-renderer';
import type {
  CompositeStep,
  FrequencyStatus,
  GuideInteractionAction,
  GuideInteractionEvent,
  GuideType,
  ResolvedGuide,
  WalkthroughCallbacks,
} from './types';
import { stepMatchesPage } from './url-match';
import { WalkthroughStateManager } from './walkthrough-state';

export interface GuidesControllerConfig {
  apiUrl: string;
  apiKey: string;
  debug?: boolean;
  /** Override del resolver (tests). */
  resolver?: GuideResolverClient;
  /** Override del tracker queue (tests). */
  trackerQueue?: GuideTrackerQueue;
  /** Override del cache (tests). */
  frequencyCache?: GuideFrequencyCache;
  /** Override del walkthrough state manager (tests). */
  walkthroughState?: WalkthroughStateManager;
}

/**
 * Ventana durante la cual un `resolve` de la MISMA `(endUserId, url)` se sirve
 * de memoria sin pegarle al backend. Corta el doble disparo identify+pageview
 * en la carga y los pageviews repetidos de una SPA (mismo URL re-montado). Las
 * guías no son tiempo-real: 30 s de staleness es imperceptible y baja el
 * volumen de `/v1/guides/resolve` drásticamente con muchos usuarios.
 */
const RESOLVE_CACHE_TTL_MS = 30_000;

/** Mapeo `action → cache status`. Solo acciones terminales mutan el cache. */
const STATUS_BY_ACTION: Record<GuideInteractionAction, FrequencyStatus | null> = {
  shown: 'shown',
  dismissed: 'dismissed',
  cta_clicked: null,
  step_advanced: null,
  step_back: null,
  completed: 'completed',
};

/**
 * Orquesta el ciclo de vida de las guías:
 *
 *   1. `pageview`/`identify` → `checkGuides(url, sessionId)`.
 *   2. Si hay un walkthrough activo (estado persistido en localStorage),
 *      tiene prioridad: se intenta resumir en la página actual antes de
 *      consultar nuevas guías. Si el selector del step actual aparece,
 *      se pinta; si no, se cancela el walkthrough limpiamente.
 *   3. Si no hay walkthrough activo, se consulta `/v1/guides/resolve` y
 *      se procesa cada guía:
 *        - `walkthrough` con varios steps → arranca un walkthrough
 *          (uno a la vez; el resto se ignora si ya hay uno).
 *        - single-step → modal/banner/tooltip como antes.
 *   4. Cada interaction actualiza el cache local Y se encola para
 *      `POST /v1/guides/:id/interactions`.
 *
 * Dedupe: `dispatched` guarda `${guideId}:${action}` ya encolados en la
 * sesión actual del SDK. Las acciones por-step de walkthroughs no
 * usan dedupe — cada `step_advanced` puede repetirse y representa una
 * navegación distinta.
 */
export class GuidesController {
  private readonly resolver: GuideResolverClient;
  private readonly trackerQueue: GuideTrackerQueue;
  private readonly frequencyCache: GuideFrequencyCache;
  private readonly walkthroughState: WalkthroughStateManager;
  private readonly debug: boolean;
  /** Para `POST /v1/guides/:id/form-response` (respuestas de guías form). */
  private readonly apiUrl: string;
  private readonly apiKey: string;
  private readonly activeRenderers = new Set<BaseRenderer>();
  /**
   * Guías single-step actualmente en pantalla, por `guideId`. Evita repintar/
   * duplicar la misma guía en cada pageview de una SPA (p.ej. una guía inline
   * anclada a un elemento persistente). Se libera al cerrarse la guía.
   */
  private readonly activeByGuideId = new Map<string, BaseRenderer>();
  private readonly dispatched = new Set<string>();
  /**
   * Renderer del paso de walkthrough activo. Puede ser el `WalkthroughRenderer`
   * (tooltip anclado) o un Modal/Banner con navegación (walkthrough heterogéneo);
   * tipamos por `destroy()`, lo único que el controller necesita.
   */
  private activeWalkthroughRenderer: { destroy(): void } | null = null;
  private activeWalkthroughGuide: ResolvedGuide | null = null;
  /** Última resolución cacheada (mismo `(endUserId, url)` → sin red). */
  private resolveCache: { key: string; at: number; guides: ResolvedGuide[] } | null = null;
  /** Resolución idéntica en vuelo, para coalescer llamadas concurrentes. */
  private inflightResolve: { key: string; promise: Promise<ResolvedGuide[]> } | null = null;
  /**
   * Disparadores por interacción armados (`on_click`/`on_hover`): guideId →
   * desarme del listener. Se re-arman desde cero en cada `checkGuides` (el
   * resolve fresco manda) y se limpian en `destroy()`.
   */
  private readonly armedTriggers = new Map<string, () => void>();
  /**
   * Tour composite ESPERANDO navegación: el usuario avanzó a un paso cuya
   * `pageLocation` apunta a otra página. Se resume en `checkGuides` cuando llega
   * a esa página (tours multi-página estilo Pendo).
   */
  private pendingComposite: { guide: ResolvedGuide; stepIndex: number } | null = null;
  /**
   * Guías composite EN PANTALLA con el paso mostrado, por `guideId`. En una SPA,
   * al navegar a una página que ya NO matchea la `pageLocation` del paso, hay que
   * DESTRUIR la guía (si no, queda pegada y "se muestra en todo lado"). Se poda
   * en cada `checkGuides` con {@link pruneComposite}.
   */
  private readonly activeComposite = new Map<
    string,
    { guide: ResolvedGuide; stepIndex: number; cleanup: () => void }
  >();

  constructor(
    private readonly client: VeoClient,
    config: GuidesControllerConfig,
  ) {
    this.debug = config.debug === true;
    this.apiUrl = config.apiUrl;
    this.apiKey = config.apiKey;
    this.resolver =
      config.resolver ?? new GuideResolverClient(config.apiUrl, config.apiKey, this.debug);

    if (config.trackerQueue) {
      this.trackerQueue = config.trackerQueue;
    } else {
      const httpClient = new GuideTrackerClient(config.apiUrl, config.apiKey);
      this.trackerQueue = new GuideTrackerQueue({
        client: httpClient,
        flushIntervalMs: DEFAULT_TRACKER_FLUSH_INTERVAL_MS,
        batchSize: DEFAULT_TRACKER_BATCH_SIZE,
        maxRetries: DEFAULT_TRACKER_MAX_RETRIES,
        onError: (err, item) => {
          if (this.debug) {
            console.warn(
              `[veo] dropped guide interaction after retries: guideId=${item.guideId} action=${item.payload.action}`,
              err,
            );
          }
        },
      });
    }

    const namespace = namespaceFromApiKey(config.apiKey);
    this.frequencyCache = config.frequencyCache ?? new GuideFrequencyCache(namespace);
    this.walkthroughState = config.walkthroughState ?? new WalkthroughStateManager(namespace);
  }

  /** Cuántas interactions hay pendientes de envío. Útil para debug en demo. */
  get pendingInteractions(): number {
    return this.trackerQueue.size;
  }

  /** Limpia el cache local (botón "reset" del demo, tests). */
  clearFrequencyCache(): void {
    this.frequencyCache.clear();
  }

  /** Limpia el walkthrough activo (debug del demo). */
  clearWalkthroughState(): void {
    this.tearDownWalkthrough();
  }

  /** Punto de entrada principal. Llamado en cada pageview/identify. */
  async checkGuides(pageUrl: string, sessionId: string): Promise<void> {
    const endUserId = this.client.getEndUserId();
    if (!endUserId) {
      if (this.debug) console.log('[veo] guides: skipped, no endUserId yet');
      return;
    }

    // Poda las guías composite en pantalla cuyo paso ya NO aplica a esta URL
    // (navegación en SPA): sin esto quedan pegadas y "se muestran en todo lado".
    this.pruneComposite(pageUrl);

    if (await this.tryResumeWalkthrough(endUserId, sessionId, pageUrl)) {
      return;
    }

    // Tour composite multi-página: si hay un paso pendiente que aplica a ESTA
    // página, resúmelo. No corta el resto del flujo (otras guías pueden mostrarse).
    this.tryResumeComposite(sessionId, pageUrl);

    let guides: ResolvedGuide[];
    try {
      guides = await this.resolveGuides(endUserId, pageUrl);
    } catch (err) {
      if (this.debug) console.error('[veo] guides resolver threw:', err);
      return;
    }
    if (this.debug) console.log(`[veo] guides: ${guides.length} eligible at ${pageUrl}`);

    // Los disparadores por interacción se re-arman desde el resolve fresco:
    // desarmar todo primero es idempotente y maneja las SPAs (la guía que ya
    // no aplica a esta URL no queda armada de una navegación anterior).
    this.disarmTriggers();

    for (const guide of guides) {
      const trigger = guide.activationRules.trigger;
      if ((trigger === 'on_click' || trigger === 'on_hover') && guide.activationRules.selector) {
        this.armInteractionTrigger(guide, endUserId, sessionId, pageUrl);
        continue;
      }
      await this.showGuide(guide, endUserId, sessionId, pageUrl);
    }
  }

  /**
   * Muestra una guía elegible (filtro de frecuencia + dedupe + dispatch por
   * tipo). Cuerpo original del loop de `checkGuides`, extraído para que los
   * disparadores por interacción pasen por LOS MISMOS chequeos al disparar.
   */
  private async showGuide(
    guide: ResolvedGuide,
    endUserId: string,
    sessionId: string,
    pageUrl: string,
  ): Promise<void> {
    if (this.frequencyCache.shouldFilter(guide.guideId, guide.displayFrequency)) {
      if (this.debug) console.log(`[veo] guides: filtered locally guideId=${guide.guideId}`);
      return;
    }

    // Ya está en pantalla → no repintar/duplicar (clave en SPAs: cada
    // navegación dispara un pageview, pero la guía sigue montada).
    if (this.activeByGuideId.has(guide.guideId)) {
      if (this.debug) console.log(`[veo] guides: already shown guideId=${guide.guideId}`);
      return;
    }

    if (guide.guideType === 'walkthrough' && guide.guideSteps.length > 1) {
      if (this.walkthroughState.hasActive()) {
        if (this.debug)
          console.log(`[veo] guides: walkthrough ${guide.guideId} skipped (another active)`);
        return;
      }
      await this.startWalkthrough(guide, endUserId, sessionId, pageUrl);
      // Las guías single-step pueden seguir mostrándose en paralelo. Solo se
      // bloquean OTROS walkthroughs vía hasActive().
      return;
    }

    // Modelo unificado: targeting de página POR-PASO + navegación multi-paso.
    if (guide.guideType === 'composite') {
      this.startComposite(guide, sessionId, pageUrl);
      return;
    }

    this.runSingleStepRender(guide, sessionId, pageUrl);
  }

  /**
   * Arma un disparador por interacción: la guía NO se muestra al cargar, sino
   * cuando el usuario hace click / pasa el mouse sobre `activationRules.selector`.
   * Listener DELEGADO en `document` (fase captura, sin preventDefault — el click
   * original de la app sigue funcionando). Al disparar se desarma y la guía pasa
   * por `showGuide` (mismos chequeos de frecuencia/dedupe). Tras cerrarse, el
   * próximo pageview/identify la re-arma — hover/click vuelve a mostrarla
   * mientras la frecuencia lo permita.
   */
  private armInteractionTrigger(
    guide: ResolvedGuide,
    endUserId: string,
    sessionId: string,
    pageUrl: string,
  ): void {
    // Armar solo lo que tiene sentido mostrar: si la frecuencia ya la filtra o
    // ya está en pantalla, no escuchamos de más.
    if (this.frequencyCache.shouldFilter(guide.guideId, guide.displayFrequency)) return;
    if (this.activeByGuideId.has(guide.guideId)) return;
    if (this.armedTriggers.has(guide.guideId)) return;

    const selector = guide.activationRules.selector as string;
    const eventName = guide.activationRules.trigger === 'on_hover' ? 'mouseover' : 'click';

    const onFire = (e: Event): void => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      try {
        if (!target.closest(selector)) return;
      } catch {
        return; // selector inválido en la DB: no romper la app
      }
      disarm();
      void this.showGuide(guide, endUserId, sessionId, pageUrl);
    };
    const disarm = (): void => {
      document.removeEventListener(eventName, onFire, true);
      this.armedTriggers.delete(guide.guideId);
    };

    document.addEventListener(eventName, onFire, true);
    this.armedTriggers.set(guide.guideId, disarm);
    if (this.debug)
      console.log(`[veo] guides: armed ${eventName} trigger for ${guide.guideId} on ${selector}`);
  }

  /** Desarma todos los disparadores por interacción pendientes. */
  private disarmTriggers(): void {
    for (const disarm of this.armedTriggers.values()) disarm();
    this.armedTriggers.clear();
  }

  /**
   * `resolve` con cache de corta duración + coalescing:
   *   - Mismo `(endUserId, url)` resuelto hace < {@link RESOLVE_CACHE_TTL_MS} →
   *     se reusa sin red. Colapsa el doble disparo identify+pageview y los
   *     pageviews repetidos de una SPA hacia la misma URL.
   *   - Si ya hay un resolve idéntico en vuelo, se espera ESE (no se abre otro).
   * El re-render sigue siendo idempotente (dedupe por `activeByGuideId` +
   * `frequencyCache`), así que reusar el resultado no duplica guías.
   */
  private async resolveGuides(endUserId: string, pageUrl: string): Promise<ResolvedGuide[]> {
    const key = `${endUserId}\n${pageUrl}`;
    const now = Date.now();

    if (this.resolveCache?.key === key && now - this.resolveCache.at < RESOLVE_CACHE_TTL_MS) {
      if (this.debug) console.log('[veo] guides: resolve cache hit');
      return this.resolveCache.guides;
    }

    if (this.inflightResolve?.key === key) {
      return this.inflightResolve.promise;
    }

    const promise = this.resolver.resolve(endUserId, pageUrl);
    this.inflightResolve = { key, promise };
    try {
      const guides = await promise;
      this.resolveCache = { key, at: Date.now(), guides };
      return guides;
    } finally {
      if (this.inflightResolve?.key === key) this.inflightResolve = null;
    }
  }

  /** Cierra todas las guías activas, limpia cache de dispatch y para timers. */
  destroy(): void {
    for (const renderer of this.activeRenderers) {
      try {
        renderer.destroy();
      } catch (err) {
        if (this.debug) console.error('[veo] renderer destroy failed:', err);
      }
    }
    this.activeRenderers.clear();
    this.activeByGuideId.clear();
    this.activeComposite.clear();
    if (this.activeWalkthroughRenderer) {
      try {
        this.activeWalkthroughRenderer.destroy();
      } catch {
        // swallow — el cleanup no debe propagar
      }
      this.activeWalkthroughRenderer = null;
      this.activeWalkthroughGuide = null;
    }
    this.disarmTriggers();
    this.dispatched.clear();
    this.resolveCache = null;
    this.inflightResolve = null;
    this.trackerQueue.destroy();
  }

  private runSingleStepRender(guide: ResolvedGuide, sessionId: string, pageUrl: string): void {
    const renderer = this.createSingleStepRenderer(guide.guideType);
    if (!renderer) return;

    // Reservar el slot YA (antes del delay) para que pageviews rápidos no
    // disparen dos renders de la misma guía.
    this.activeByGuideId.set(guide.guideId, renderer);

    const delayMs = guide.activationRules.delayMs ?? 0;
    window.setTimeout(
      () => this.mountSingleStepRenderer(guide, renderer, sessionId, pageUrl),
      delayMs,
    );
  }

  /**
   * Camino del modelo unificado (`composite`). Elige el PRIMER paso cuya
   * `pageLocation` matchea la URL actual (targeting por-paso) y lo pinta con el
   * `CompositeRenderer`. Si ningún paso aplica a esta página, no muestra nada.
   */
  private startComposite(guide: ResolvedGuide, sessionId: string, pageUrl: string): void {
    // Ya hay un tour de esta guía a mitad de camino (esperando navegación) → no
    // reiniciar desde el paso 0; el resume lo maneja `tryResumeComposite`.
    if (this.pendingComposite?.guide.guideId === guide.guideId) return;
    const steps = guide.guideSteps as unknown as CompositeStep[];
    const start = steps.findIndex((s) => stepMatchesPage(s.pageLocation, pageUrl));
    if (start === -1) return; // ningún paso aplica a esta URL
    this.renderCompositeStep(guide, start, sessionId, pageUrl);
  }

  /**
   * Destruye las guías composite en pantalla cuyo paso mostrado ya no matchea la
   * URL actual (el usuario navegó fuera de su `pageLocation` en una SPA). Sin
   * esto la guía queda montada en el DOM y aparece en páginas que no debía.
   */
  private pruneComposite(pageUrl: string): void {
    for (const entry of this.activeComposite.values()) {
      const steps = entry.guide.guideSteps as unknown as CompositeStep[];
      const step = steps[entry.stepIndex];
      if (!step || !stepMatchesPage(step.pageLocation, pageUrl)) {
        entry.cleanup(); // borra de activeComposite/activeByGuideId y destruye
      }
    }
  }

  /**
   * Resume un tour composite que quedó esperando navegación: si el paso
   * pendiente aplica a la página actual, lo pinta. Devuelve `true` si resumió.
   */
  private tryResumeComposite(sessionId: string, pageUrl: string): boolean {
    const pending = this.pendingComposite;
    if (!pending) return false;
    const steps = pending.guide.guideSteps as unknown as CompositeStep[];
    const step = steps[pending.stepIndex];
    if (!step) {
      this.pendingComposite = null;
      return false;
    }
    if (!stepMatchesPage(step.pageLocation, pageUrl)) return false; // aún no llegó
    this.pendingComposite = null;
    this.renderCompositeStep(pending.guide, pending.stepIndex, sessionId, pageUrl);
    return true;
  }

  /**
   * Pinta UN paso composite. En guías multi-paso pasa `nav` al renderer para
   * que los botones next/prev avancen (misma página); en single-step los
   * botones cierran. Cada avance destruye el paso anterior y monta el siguiente.
   */
  private renderCompositeStep(
    guide: ResolvedGuide,
    idx: number,
    sessionId: string,
    pageUrl: string,
  ): void {
    const steps = guide.guideSteps as unknown as CompositeStep[];
    const renderer = new CompositeRenderer();
    this.activeRenderers.add(renderer);
    this.activeByGuideId.set(guide.guideId, renderer);

    const cleanup = (): void => {
      renderer.destroy();
      this.activeRenderers.delete(renderer);
      if (this.activeByGuideId.get(guide.guideId) === renderer) {
        this.activeByGuideId.delete(guide.guideId);
      }
      this.activeComposite.delete(guide.guideId);
    };
    // Registrar el paso mostrado para poder podarlo si el usuario navega a una
    // página que ya no lo matchea (targeting por-paso en SPAs).
    this.activeComposite.set(guide.guideId, { guide, stepIndex: idx, cleanup });

    // Avanza a `target`: si ese paso aplica a la página ACTUAL lo pinta ya
    // (tour misma-página); si apunta a OTRA página, guarda el pendiente y espera
    // a que el usuario navegue (tour multi-página, se resume en `checkGuides`).
    const goToStep = (target: number): void => {
      cleanup();
      if (target < 0 || target >= steps.length) return; // fuera de rango → fin
      const step = steps[target];
      if (step && stepMatchesPage(step.pageLocation, pageUrl)) {
        this.renderCompositeStep(guide, target, sessionId, pageUrl);
      } else {
        this.pendingComposite = { guide, stepIndex: target };
      }
    };

    const nav =
      steps.length > 1
        ? {
            stepIndex: idx,
            totalSteps: steps.length,
            callbacks: {
              onNext: (): void => goToStep(idx + 1),
              onBack: (): void => goToStep(idx - 1),
              onGoto: (target: number): void => goToStep(target),
              onSkip: cleanup,
              onComplete: cleanup,
            },
          }
        : undefined;

    try {
      void renderer.render({
        guide,
        onInteraction: (e) => this.handleInteraction(guide, sessionId, pageUrl, e),
        onClose: cleanup,
        onTrack: (name, props) => this.client.track(name, props),
        onFormSubmit: (answers) => this.submitFormResponse(guide.guideId, answers),
        onOpenGuide: (targetGuideId) => {
          void this.openGuideById(targetGuideId, sessionId, pageUrl);
        },
        ...(nav ? { nav } : {}),
      });
    } catch (err) {
      if (this.debug) console.error('[veo] composite renderer threw:', err);
      cleanup();
    }
  }

  private mountSingleStepRenderer(
    guide: ResolvedGuide,
    renderer: BaseRenderer,
    sessionId: string,
    pageUrl: string,
  ): void {
    this.activeRenderers.add(renderer);
    this.activeByGuideId.set(guide.guideId, renderer);

    const onClose = (): void => {
      renderer.destroy();
      this.activeRenderers.delete(renderer);
      if (this.activeByGuideId.get(guide.guideId) === renderer) {
        this.activeByGuideId.delete(guide.guideId);
      }
    };

    const onInteraction = (event: GuideInteractionEvent): void => {
      this.handleInteraction(guide, sessionId, pageUrl, event);
    };

    const onTrack = (eventName: string, properties?: Record<string, unknown>): void => {
      this.client.track(eventName, properties);
    };

    const onFormSubmit = (answers: Record<string, unknown>): Promise<boolean> =>
      this.submitFormResponse(guide.guideId, answers);

    // Encadenado: un botón `action:'guide'` abre otra guía por id.
    const onOpenGuide = (targetGuideId: string): void => {
      void this.openGuideById(targetGuideId, sessionId, pageUrl);
    };

    try {
      void renderer.render({ guide, onInteraction, onClose, onTrack, onFormSubmit, onOpenGuide });
    } catch (err) {
      if (this.debug) console.error('[veo] renderer.render threw:', err);
      this.activeRenderers.delete(renderer);
      if (this.activeByGuideId.get(guide.guideId) === renderer) {
        this.activeByGuideId.delete(guide.guideId);
      }
    }
  }

  /**
   * Abre una guía POR ID, disparada desde el botón de otra guía (encadenado).
   * Ignora las URL rules y el trigger (el autor lo pidió explícitamente desde
   * un botón); respeta el dedupe de guías activas y el flujo normal de
   * tracking. 404/red → no-op (la guía destino pudo borrarse).
   */
  private async openGuideById(guideId: string, sessionId: string, pageUrl: string): Promise<void> {
    if (this.activeByGuideId.has(guideId)) return;
    const guide = await this.resolver.fetchById(guideId);
    if (!guide) {
      if (this.debug) console.warn(`[veo] guides: chained guide ${guideId} not found`);
      return;
    }
    const endUserId = this.client.getEndUserId() ?? this.client.getAnonymousId();
    if (guide.guideType === 'walkthrough' && guide.guideSteps.length > 1) {
      if (!this.walkthroughState.hasActive()) {
        await this.startWalkthrough(guide, endUserId, sessionId, pageUrl);
      }
      return;
    }
    this.runSingleStepRender(guide, sessionId, pageUrl);
  }

  /**
   * Si hay un walkthrough persistido, intenta pintar el step actual en
   * la página actual. Retorna `true` si lo intentó (sea éxito o cancel),
   * `false` si no había walkthrough activo.
   *
   * Cancela limpiamente (sin enviar `dismissed` falso) cuando:
   *   - La guía fue eliminada en el backend (404 → `fetchById` devuelve null).
   *   - La guía cambió de tipo y ya no es walkthrough.
   *   - El selector del step actual no aparece dentro del timeout.
   */
  private async tryResumeWalkthrough(
    endUserId: string,
    sessionId: string,
    pageUrl: string,
  ): Promise<boolean> {
    const state = this.walkthroughState.current();
    if (!state) return false;

    const guide = await this.resolver.fetchById(state.guideId);
    if (!guide || guide.guideType !== 'walkthrough') {
      if (this.debug)
        console.log(`[veo] guides: walkthrough ${state.guideId} no longer available, cleaning up`);
      this.tearDownWalkthrough();
      return true;
    }

    // El cache local podría tener una versión más vieja del total; usamos
    // siempre el del backend.
    const stepIndex = Math.min(state.currentStepIndex, guide.guideSteps.length - 1);
    await this.renderWalkthroughStep(guide, stepIndex, endUserId, sessionId, pageUrl);
    return true;
  }

  private async startWalkthrough(
    guide: ResolvedGuide,
    endUserId: string,
    sessionId: string,
    pageUrl: string,
  ): Promise<void> {
    this.walkthroughState.start(guide.guideId, guide.guideSteps.length);
    const rendered = await this.renderWalkthroughStep(guide, 0, endUserId, sessionId, pageUrl);
    if (!rendered) return; // teardown ya corrió, no reportamos shown.
    // Reportamos `shown` SOLO al arrancar (step 0). Steps siguientes
    // se cubren con `step_advanced`/`step_back`. Marcamos cache local
    // también para que `displayFrequency='once'` filtre en la próxima
    // pageview aunque la interaction aún esté en la queue.
    this.enqueueInteractionOnce(guide.guideId, endUserId, sessionId, pageUrl, 'shown', 0);
    this.frequencyCache.record(guide.guideId, 'shown');
  }

  /**
   * Pinta el step indicado. Reusa la guía (no fetcha): el caller debe
   * traerla. Retorna `true` si el render fue exitoso. En caso contrario
   * ya disparó el teardown — el caller no debe seguir reportando.
   */
  private async renderWalkthroughStep(
    guide: ResolvedGuide,
    stepIndex: number,
    endUserId: string,
    sessionId: string,
    pageUrl: string,
  ): Promise<boolean> {
    if (this.activeWalkthroughRenderer) {
      this.activeWalkthroughRenderer.destroy();
      this.activeWalkthroughRenderer = null;
    }

    this.activeWalkthroughGuide = guide;

    const callbacks: WalkthroughCallbacks = {
      onNext: () => this.handleWalkthroughNext(endUserId, sessionId, pageUrl),
      onBack: () => this.handleWalkthroughBack(endUserId, sessionId, pageUrl),
      onSkip: () => this.handleWalkthroughSkip(endUserId, sessionId, pageUrl),
      onComplete: () => this.handleWalkthroughComplete(endUserId, sessionId, pageUrl),
    };

    // Walkthrough HETEROGÉNEO: un paso puede pedir renderizarse como modal o
    // banner (via `step.style.render`). Esos renderers siempre montan (no
    // dependen de un ancla), así que reportamos éxito directo. El resto
    // (tooltip/default) usa el WalkthroughRenderer anclado.
    const step = guide.guideSteps[stepIndex];
    const render = readStepRender(step);
    if (render === 'modal' || render === 'banner') {
      const renderer = render === 'modal' ? new ModalRenderer() : new BannerRenderer();
      try {
        renderer.render({
          guide,
          onInteraction: () => {},
          onClose: () => {},
          nav: { stepIndex, totalSteps: guide.guideSteps.length, callbacks },
        });
      } catch (err) {
        if (this.debug) console.error('[veo] sequence renderer threw:', err);
        this.tearDownWalkthrough();
        return false;
      }
      this.activeWalkthroughRenderer = renderer;
      return true;
    }

    const renderer = new WalkthroughRenderer();
    let success = false;
    try {
      success = await renderer.render({ guide, currentStepIndex: stepIndex, callbacks });
    } catch (err) {
      if (this.debug) console.error('[veo] walkthrough renderer threw:', err);
    }

    if (!success) {
      if (this.debug)
        console.warn(
          `[veo] walkthrough ${guide.guideId} step ${stepIndex} could not render, cancelling`,
        );
      this.tearDownWalkthrough();
      return false;
    }

    this.activeWalkthroughRenderer = renderer;
    return true;
  }

  private handleWalkthroughNext(endUserId: string, sessionId: string, pageUrl: string): void {
    const guide = this.activeWalkthroughGuide;
    if (!guide) return;

    const newState = this.walkthroughState.advance();
    if (!newState) {
      // Ya estábamos en el último step. No debería pasar (el botón
      // dispara onComplete en ese caso), pero por defensa lo manejamos.
      this.handleWalkthroughComplete(endUserId, sessionId, pageUrl);
      return;
    }

    this.enqueueInteraction(
      guide.guideId,
      endUserId,
      sessionId,
      pageUrl,
      'step_advanced',
      newState.currentStepIndex,
    );
    void this.renderWalkthroughStep(
      guide,
      newState.currentStepIndex,
      endUserId,
      sessionId,
      pageUrl,
    );
  }

  private handleWalkthroughBack(endUserId: string, sessionId: string, pageUrl: string): void {
    const guide = this.activeWalkthroughGuide;
    if (!guide) return;

    const state = this.walkthroughState.current();
    if (!state || state.currentStepIndex === 0) return;

    const newState = this.walkthroughState.back();
    if (!newState) return;

    this.enqueueInteraction(
      guide.guideId,
      endUserId,
      sessionId,
      pageUrl,
      'step_back',
      newState.currentStepIndex,
    );
    void this.renderWalkthroughStep(
      guide,
      newState.currentStepIndex,
      endUserId,
      sessionId,
      pageUrl,
    );
  }

  private handleWalkthroughSkip(endUserId: string, sessionId: string, pageUrl: string): void {
    const guide = this.activeWalkthroughGuide;
    const state = this.walkthroughState.current();
    if (!guide || !state) return;

    this.enqueueInteraction(
      guide.guideId,
      endUserId,
      sessionId,
      pageUrl,
      'dismissed',
      state.currentStepIndex,
    );
    this.frequencyCache.record(guide.guideId, 'dismissed');
    this.tearDownWalkthrough();
  }

  private handleWalkthroughComplete(endUserId: string, sessionId: string, pageUrl: string): void {
    const guide = this.activeWalkthroughGuide;
    const state = this.walkthroughState.current();
    if (!guide || !state) return;

    this.enqueueInteraction(
      guide.guideId,
      endUserId,
      sessionId,
      pageUrl,
      'completed',
      state.currentStepIndex,
    );
    this.frequencyCache.record(guide.guideId, 'completed');
    this.tearDownWalkthrough();
  }

  private tearDownWalkthrough(): void {
    if (this.activeWalkthroughRenderer) {
      try {
        this.activeWalkthroughRenderer.destroy();
      } catch {
        // swallow
      }
    }
    this.activeWalkthroughRenderer = null;
    this.activeWalkthroughGuide = null;
    this.walkthroughState.clear();
  }

  /**
   * Tras cada interaction single-step:
   *   1. dedupe (no enviar 2× la misma `(guideId, action)` por sesión)
   *   2. actualizar el cache local si corresponde
   *   3. encolar el POST
   *
   * El endUserId se lee del client EN ESTE MOMENTO (no en el render),
   * por si el usuario se identificó después de que la guía apareció.
   */
  private handleInteraction(
    guide: ResolvedGuide,
    sessionId: string,
    pageUrl: string,
    event: GuideInteractionEvent,
  ): void {
    // Con varios botones por paso, cada botón cuenta aparte: el buttonId entra
    // a la key (si no, el segundo botón clickeado se perdería por dedup).
    const buttonKey = event.metadata?.buttonId ? `:${event.metadata.buttonId}` : '';
    const dedupKey = `${guide.guideId}:${event.action}${buttonKey}`;
    if (this.dispatched.has(dedupKey)) {
      if (this.debug) console.log(`[veo] guides: dedup hit ${dedupKey}`);
      return;
    }
    this.dispatched.add(dedupKey);

    const cacheStatus = STATUS_BY_ACTION[event.action];
    if (cacheStatus) this.frequencyCache.record(guide.guideId, cacheStatus);

    const endUserId = this.client.getEndUserId();
    if (!endUserId) {
      if (this.debug)
        console.warn('[veo] guides: interaction without endUserId — skipping enqueue');
      return;
    }

    this.trackerQueue.enqueue({
      guideId: guide.guideId,
      payload: {
        endUserId,
        sessionId,
        action: event.action,
        stepIndex: event.stepIndex,
        pageUrl,
        pagePath: extractPath(pageUrl),
        ...(event.metadata ? { metadata: event.metadata } : {}),
      },
    });
  }

  /**
   * Encola una interacción de walkthrough SIN pasar por el dedup set.
   * Acciones como `step_advanced` se repiten naturalmente (cada next es
   * una navegación distinta) y deduplicarlas perdería datos.
   */
  private enqueueInteraction(
    guideId: string,
    endUserId: string,
    sessionId: string,
    pageUrl: string,
    action: GuideInteractionAction,
    stepIndex: number,
  ): void {
    this.trackerQueue.enqueue({
      guideId,
      payload: {
        endUserId,
        sessionId,
        action,
        stepIndex,
        pageUrl,
        pagePath: extractPath(pageUrl),
      },
    });
  }

  /**
   * Encola con dedup por sesión del SDK. Para `shown` del primer step:
   * no queremos re-emitirlo si el usuario navega entre dos páginas que
   * matchean el mismo step.
   */
  private enqueueInteractionOnce(
    guideId: string,
    endUserId: string,
    sessionId: string,
    pageUrl: string,
    action: GuideInteractionAction,
    stepIndex: number,
  ): void {
    const dedupKey = `${guideId}:${action}:${stepIndex}`;
    if (this.dispatched.has(dedupKey)) return;
    this.dispatched.add(dedupKey);
    this.enqueueInteraction(guideId, endUserId, sessionId, pageUrl, action, stepIndex);
  }

  private createSingleStepRenderer(type: GuideType): BaseRenderer | null {
    switch (type) {
      case 'modal':
        return new ModalRenderer();
      case 'banner':
        return new BannerRenderer();
      case 'tooltip':
        return new TooltipRenderer();
      case 'custom':
        return new CustomRenderer();
      case 'inline':
        return new InlineRenderer();
      case 'inline-custom':
        return new InlineCustomRenderer();
      case 'form':
        return new FormRenderer();
      case 'inline-form':
        return new InlineFormRenderer();
      case 'badge':
        return new BadgeRenderer();
      case 'composite':
        return new CompositeRenderer();
      case 'walkthrough':
        return null;
      default:
        return null;
    }
  }

  /**
   * Envía las respuestas de una guía `form` al backend, que las mergea como
   * propiedades del usuario. Usa el endUserId actual (o el anónimo si aún no
   * hubo identify — el backend pre-crea la fila igual que identify).
   */
  private async submitFormResponse(
    guideId: string,
    answers: Record<string, unknown>,
  ): Promise<boolean> {
    const endUserId = this.client.getEndUserId() ?? this.client.getAnonymousId();
    try {
      const res = await fetch(`${this.apiUrl}/v1/guides/${guideId}/form-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Api-Key': this.apiKey },
        body: JSON.stringify({ endUserId, answers }),
      });
      if (!res.ok && this.debug) {
        console.warn('[veo] form-response respondió', res.status);
      }
      return res.ok;
    } catch (err) {
      if (this.debug) console.warn('[veo] form-response falló:', err);
      return false;
    }
  }
}

function extractPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return '/';
  }
}

/**
 * Cómo se presenta un paso dentro de un walkthrough. `style.render` ∈
 * `modal`/`banner` usa esos renderers (con navegación); cualquier otro valor
 * (incluido ausente o `tooltip`) usa el WalkthroughRenderer anclado.
 */
function readStepRender(step: ResolvedGuide['guideSteps'][number] | undefined): string {
  const r = (step?.style as Record<string, unknown> | null | undefined)?.render;
  return typeof r === 'string' ? r : 'walkthrough';
}
